// Profile: who is signed in, their picture, a link to Keycloak's account console to change details, and sign out.
// A modal over the current page, opened from the account menu.
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { GarminConnection } from '@/features/garmin/GarminControls';
import { Avatar } from '@/people/Avatar';
import { usePersonPicture, useSetPersonPicture } from '@/people/pictures';
import { useAccount } from './useAccount';
import styles from './ProfileDialog.module.css';

export function ProfileDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const account = useAccount();
  const dialog = useRef<HTMLDialogElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const picture = usePersonPicture(account.personGuid);
  const setPicture = useSetPersonPicture(account.personGuid);

  // A native modal dialog: the focus trap, Escape and the backdrop come from the browser.
  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (open && !element.open) element.showModal();
    if (!open && element.open) element.close();
  }, [open]);

  const choosePicture = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // so choosing the same file again still fires
    if (file) setPicture.mutate(file);
  };

  const rows = [
    { label: t('profile.name'), value: account.name },
    { label: t('profile.username'), value: account.username },
    {
      label: t('profile.email'),
      value:
        account.email &&
        `${account.email}${account.emailVerified === false ? ` (${t('profile.notVerified')})` : ''}`,
    },
  ].filter((row) => row.value);

  return (
    <dialog
      ref={dialog}
      className={styles.dialog}
      aria-labelledby="profile-title"
      onClose={onClose}
      // A click on the backdrop lands on the dialog element itself.
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className={styles.header}>
        <h2 id="profile-title">{t('profile.title')}</h2>
        <button type="button" className={styles.close} aria-label={t('profile.close')} onClick={onClose}>
          ×
        </button>
      </div>

      <div className={styles.picture}>
        <Avatar personGuid={account.personGuid} initials={account.initials} size={96} />
        {account.personGuid && (
          <div className={styles.pictureActions}>
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              hidden
              onChange={choosePicture}
            />
            <button
              type="button"
              className={styles.button}
              disabled={setPicture.isPending}
              onClick={() => fileInput.current?.click()}
            >
              {setPicture.isPending
                ? t('profile.picture.saving')
                : picture
                  ? t('profile.picture.change')
                  : t('profile.picture.upload')}
            </button>
            {picture && (
              <button
                type="button"
                className={styles.link}
                disabled={setPicture.isPending}
                onClick={() => setPicture.mutate(null)}
              >
                {t('profile.picture.remove')}
              </button>
            )}
            {setPicture.isError && (
              <p role="alert" className={styles.error}>
                {t('profile.picture.error')}
              </p>
            )}
          </div>
        )}
      </div>

      <dl className={styles.details}>
        {rows.map((row) => (
          <div key={row.label} className={styles.row}>
            <dt>{row.label}</dt>
            <dd>{row.value}</dd>
          </div>
        ))}
      </dl>
      {account.personGuid && open && <GarminConnection />}
      <div className={styles.actions}>
        {account.accountUrl && (
          <a className={styles.button} href={account.accountUrl} target="_blank" rel="noreferrer">
            {t('profile.manage')}
          </a>
        )}
        <button type="button" className={styles.primary} onClick={account.signOut}>
          {t('nav.signOut')}
        </button>
      </div>
    </dialog>
  );
}
